<template>
    <MainContent
        title="Listado de eventos"
        description="Gestiona los eventos, sesiones y ventas del teatro: eventos activos y pausados, el histórico de eventos pasados y el resumen de ventas y arqueo de caja."
    >
        <template #header>
            <!-- Card única: tabs + buscador + acciones -->
            <Card custom-classes="tw:mb-4" :padding="0">
                <TabsPanel
                    bare
                    :bordered="false"
                    :tabs="tabs"
                    :initial-tab="tab"
                    @tab-change="onTabChange"
                />

                <div class="tw:border-t tw:border-gray-200 tw:p-4">
                    <div class="tw:flex tw:flex-col tw:lg:flex-row tw:lg:items-center tw:gap-3 tw:justify-between">
                        <div v-if="mostrarBuscador" class="tw:w-full tw:lg:max-w-md">
                            <SearchComponent
                                :key="searchResetToken"
                                :value="search"
                                placeholder="Buscar evento"
                                hide-label
                                data-cy="search-eventos"
                                @input="onSearchInput"
                            />
                        </div>
                        <!-- En fila (sm+) los botones se alinean a la derecha; en
                             columna (móvil) ocupan el ancho. `lg:shrink-0` evita
                             que se aplasten cuando la barra entra en una sola
                             línea: el buscador es quien cede ancho. -->
                        <div class="tw:flex tw:flex-col tw:sm:flex-row tw:sm:justify-end tw:gap-3 tw:w-full tw:lg:w-auto tw:lg:ml-auto tw:lg:shrink-0">
                            <!--
                                En la pestaña de resumen de ventas las acciones de
                                página son las descargas (así lo pide el diseño):
                                el informe se arma con los filtros de la pestaña.
                            -->
                            <template v-if="tab === 'resumen_ventas'">
                                <Button
                                    icon="fas fa-file-download"
                                    custom-class="tw:justify-center"
                                    data-cy="descargar-resumen"
                                    @click="abrirModal('descargar-resumen-ventas')"
                                >
                                    Descargar resumen
                                </Button>
                                <Button
                                    variant="primary"
                                    icon="fas fa-cash-register"
                                    custom-class="tw:justify-center"
                                    data-cy="descargar-arqueo-caja"
                                    @click="abrirModal('descargar-arqueo-caja')"
                                >
                                    Descargar arqueo caja
                                </Button>
                            </template>
                            <template v-else>
                                <Button
                                    v-if="mostrarPacks"
                                    as="router-link"
                                    :to="{ name: 'back.packs-events.index' }"
                                    icon="fas fa-box"
                                    custom-class="tw:justify-center"
                                >
                                    Packs de eventos
                                </Button>
                                <Button
                                    v-if="permisos['eventos.crear']"
                                    as="router-link"
                                    :to="{ name: 'back.events.crear' }"
                                    variant="primary"
                                    icon="fas fa-plus"
                                    custom-class="tw:justify-center"
                                >
                                    Crear evento
                                </Button>
                            </template>
                        </div>
                    </div>
                </div>
            </Card>
        </template>

        <EventosActuales
            v-if="tab === 'actuales'"
            :search="search"
            :page-reset-token="pageResetToken"
            @changed="fetchTotales"
        />
        <EventosPasados
            v-else-if="tab === 'pasados'"
            :search="search"
            :page-reset-token="pageResetToken"
            @changed="fetchTotales"
        />
        <ResumenVentas
            v-else-if="tab === 'resumen_ventas'"
            @filters-change="resumenFiltros = $event"
            @catalogos-change="resumenCatalogos = $event"
        />

        <!-- Descargas del resumen de ventas: viven en la cabecera de la página. -->
        <template v-if="tab === 'resumen_ventas'">
            <DescargarResumenVentasModal
                name="descargar-resumen-ventas"
                :filtros="resumenFiltros"
                :teatros="resumenCatalogos.teatros"
                :taquilleros="resumenCatalogos.taquilleros"
                :companias="resumenCatalogos.companias"
                @close="cerrarModal('descargar-resumen-ventas')"
            />
            <DescargarArqueoCajaModal
                name="descargar-arqueo-caja"
                :filtros="resumenFiltros"
                :teatros="resumenCatalogos.teatros"
                :taquilleros="resumenCatalogos.taquilleros"
                :companias="resumenCatalogos.companias"
                @close="cerrarModal('descargar-arqueo-caja')"
            />
        </template>
    </MainContent>
</template>

<script setup>
    import { ref, computed, watch, onMounted, nextTick, getCurrentInstance } from 'vue';
    import store from '@/store';

    import { useRoute, useRouter } from '@/composables/common/router/useRouter';
    import { useEventosCounts } from '@/composables/admin/eventos/useEventosCounts';

    import MainContent from '@/modules/admin/ui/MainContent.vue';
    import Card from '@/modules/admin/ui/Card.vue';
    import TabsPanel from '@/modules/admin/ui/TabsPanel.vue';
    import SearchComponent from '@/modules/admin/ui/SearchComponent.vue';
    import Button from '@/modules/admin/ui/Button.vue';

    import EventosActuales from '@/modules/admin/eventos/EventosActuales.vue';
    import EventosPasados from '@/components/backend/eventos/EventosPasados';
    import ResumenVentas from '@/modules/admin/eventos/resumen-ventas/Index.vue';
    import DescargarResumenVentasModal from '@/modules/admin/eventos/resumen-ventas/modals/DescargarResumenVentasModal.vue';
    import DescargarArqueoCajaModal from '@/modules/admin/eventos/resumen-ventas/modals/DescargarArqueoCajaModal.vue';

    const instance = getCurrentInstance();
    const route = useRoute();
    const router = useRouter();
    const getCurrentRoute = () => instance.proxy.$route || router.currentRoute || route;

    const permisos = computed(() => store.getters.getPermisos);

    function tabDesdeQuery(value) {
        if (value === 'pasados') return 'pasados';
        if (value === 'resumen_ventas' && permisos.value['taquilleros.gestion']) return value;
        return 'actuales';
    }

    const tab = ref(tabDesdeQuery(getCurrentRoute().query.tab));
    const pageResetToken = ref(0);
    const searchResetToken = ref(0);
    let suppressSearchUrlSync = false;

    async function persistTab(value, resetList) {
        const query = { ...getCurrentRoute().query };

        if (value === 'actuales') delete query.tab;
        else query.tab = value;

        if (resetList) {
            delete query.page;
            delete query.search;
        }

        await router.replace({ query }).catch(() => {});
    }

    async function onTabChange(value) {
        const resetList = ['actuales', 'pasados'].includes(value);

        if (resetList) {
            if (value === tab.value) pageResetToken.value++;
            searchResetToken.value++;

            if (search.value) {
                suppressSearchUrlSync = true;
                search.value = '';
            }

            // Espera a que el listado activo aplique page=1 y search vacío
            // antes de cambiar de componente o sincronizar la URL.
            await nextTick();
            await nextTick();
        }

        await persistTab(value, resetList);
        tab.value = value;
    }

    /**
     * `input` llega ya debounced (500 ms) con el término aplicado. El listado
     * refleja su propia carga con el skeleton; el buscador mantiene la lupa
     * fija y no duplica el estado de carga.
     */
    function onSearchInput(value) {
        search.value = value;
    }

    /**
     * El término de búsqueda vive en la URL (`?search=`): recargar o compartir
     * el enlace conserva el listado filtrado. Se escribe cuando el buscador ya
     * ha debounced (SearchComponent emite a los 500 ms), no en cada tecla.
     */
    const search = ref(
        typeof route.query.search === 'string' ? route.query.search : ''
    );

    watch(search, value => {
        if (suppressSearchUrlSync) {
            suppressSearchUrlSync = false;
            return;
        }

        const query = { ...getCurrentRoute().query };

        if (value) query.search = value;
        else delete query.search;

        // Buscar vuelve siempre a la primera página: el hijo resetea `page` y
        // la URL no debe conservar la anterior.
        delete query.page;

        router.replace({ query }).catch(() => {});
    });

    // Atrás/adelante: manda la URL.
    watch(() => getCurrentRoute()?.query?.search, value => {
        const next = typeof value === 'string' ? value : '';

        if (next !== search.value) search.value = next;
    });

    watch(() => getCurrentRoute()?.query?.tab, value => {
        const next = tabDesdeQuery(value);

        if (next !== tab.value) tab.value = next;
    });

    /**
     * Totales de las dos pestañas. Se piden al montar (una sola petición
     * ligera) y cada vez que un hijo avisa de una mutación: crear, borrar o
     * cambiar de fecha un evento mueve los dos contadores. Buscar o paginar no
     * los mueve, así que no se vuelven a pedir en esos casos.
     */
    const { totalActuales, totalPasados, fetchTotales } = useEventosCounts();

    onMounted(fetchTotales);

    /**
     * Estado que la pestaña de resumen de ventas publica hacia la cabecera:
     * los filtros vigentes y sus catálogos. La cabecera los necesita para las
     * descargas, que son acciones de PÁGINA (viven fuera de la pestaña), y así
     * no se vuelven a pedir los catálogos ni se duplica el estado.
     */
    const resumenFiltros = ref({});
    const resumenCatalogos = ref({ teatros: [], taquilleros: [], companias: [] });

    function abrirModal(name) {
        instance.proxy.$modal.show(name);
    }

    function cerrarModal(name) {
        instance.proxy.$modal.hide(name);
    }

    const teatro = computed(() => store.getters.getTeatro);
    const footer = computed(() => store.getters.getFooter);

    const mostrarPacks = computed(() =>
        teatro.value?.tipo == 1 || (teatro.value?.tipo == 2 && !footer.value?.ocultar_pack)
    );

    /**
     * El buscador solo filtra los listados de eventos (actuales y pasados).
     * En la pestaña de resumen de ventas no aplica, así que se oculta en vez
     * de mostrarse deshabilitado como un control muerto.
     */
    const mostrarBuscador = computed(() => tab.value !== 'resumen_ventas');

    const tabs = computed(() => [
        { key: 'actuales', label: 'Eventos actuales', icon: 'far fa-calendar-alt', count: totalActuales.value },
        { key: 'pasados', label: 'Eventos pasados', icon: 'fas fa-history', count: totalPasados.value },
        ...(permisos.value['taquilleros.gestion']
            ? [{ key: 'resumen_ventas', label: 'Resumen de ventas & Arqueo', icon: 'fas fa-cash-register' }]
            : []),
    ]);
</script>
